import { ArrowDown, ArrowUp } from "lucide-react";
import { useState } from "react";
import { ListBox } from "react-aria-components";
import type { Size } from "../../utils/controlStyles";
import { Button } from "../Button";
import { ListBoxItem } from "../ListBoxItem";
import { Select } from "../Select";
import { Tray } from "../Tray";

export interface SortOption {
  value: string;
  label: string;
}

export interface SortControlProps {
  /** Currently selected sort value (field name) */
  value: string;
  /** Available sort options */
  options: SortOption[];
  /** Callback when sort selection changes */
  onChange: (value: string) => void;
  /** Sort direction (optional, for enhanced mode) */
  direction?: "asc" | "desc";
  /** Callback when direction changes (optional, for enhanced mode) */
  onDirectionChange?: (direction: "asc" | "desc") => void;
  /** Whether to show "Sort by:" label */
  showLabel?: boolean;
  /** Size variant */
  size?: Size;
  /** Additional CSS classes */
  className?: string;
  /** Use compact icon-only mode (useful for narrow layouts) */
  compact?: boolean;
}

/**
 * SortControl
 *
 * A dropdown for selecting sort order with optional direction toggle.
 * Wraps Select component with consistent styling for data controls.
 *
 * **Basic Mode (backward compatible):**
 * ```tsx
 * <SortControl
 *   value={sortBy}
 *   options={[
 *     { value: 'name', label: 'Name' },
 *     { value: 'date', label: 'Date' }
 *   ]}
 *   onChange={setSortBy}
 * />
 * ```
 *
 * **Enhanced Mode (with direction):**
 * ```tsx
 * <SortControl
 *   value={sortField}
 *   direction={sortDirection}
 *   options={[
 *     { value: 'name', label: 'Name' },
 *     { value: 'date', label: 'Date' }
 *   ]}
 *   onChange={setSortField}
 *   onDirectionChange={setSortDirection}
 * />
 * ```
 *
 * **Compact Mode (icon-only, for narrow layouts):**
 * ```tsx
 * <SortControl
 *   value={sortBy}
 *   options={sortOptions}
 *   onChange={setSortBy}
 *   compact
 * />
 * ```
 */
export function SortControl({
  value,
  options,
  onChange,
  direction,
  onDirectionChange,
  showLabel = true,
  size = "sm",
  className = "",
  compact = false,
}: SortControlProps) {
  const [isTrayOpen, setIsTrayOpen] = useState(false);
  const hasDirectionControl = direction !== undefined && onDirectionChange !== undefined;
  const selectedOption = options.find((opt) => opt.value === value);

  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Button
          variant="secondary"
          size={size}
          icon="ArrowsDownUp"
          aria-label={`Sort by: ${selectedOption?.label || "Select"}`}
          onPress={() => setIsTrayOpen(true)}
        />
        <Tray isOpen={isTrayOpen} onClose={() => setIsTrayOpen(false)} title="Sort by" contentPadding={false}>
          <ListBox
            items={options.map((option) => ({
              id: option.value,
              label: option.label,
              value: option.value,
            }))}
            className="w-full bg-background-surface p-0 outline-0 [&_[role=option]]:border-b [&_[role=option]]:border-border-default [&_[role=option]:last-child]:border-0 [&_[role=option]]:!px-6 [&_[role=option]]:!py-4 [&_[role=option]]:rounded-none"
            selectionMode="single"
            selectedKeys={value ? [value] : []}
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0];
              if (key !== undefined && key !== null) {
                onChange(key as string);
                setIsTrayOpen(false);
              }
            }}
          >
            {(item) => (
              <ListBoxItem key={item.id} id={item.id} textValue={item.label}>
                <span className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-1">{item.label}</span>
              </ListBoxItem>
            )}
          </ListBox>
        </Tray>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showLabel && <span className="text-sm text-text-secondary whitespace-nowrap">Sort by:</span>}
      <Select
        selectedKey={value}
        onSelectionChange={(key) => onChange(key as string)}
        size={size}
        className="min-w-32"
        items={options.map((option) => ({
          id: option.value,
          label: option.label,
          value: option.value,
        }))}
        renderItem={(item) => item.label}
        aria-label="Sort by"
        reserveErrorSpace={false}
      />
      {hasDirectionControl && (
        <Button
          variant="ghost"
          size={size}
          onClick={() => onDirectionChange(direction === "asc" ? "desc" : "asc")}
          aria-label={`Sort ${direction === "asc" ? "descending" : "ascending"}`}
          className="p-1.5"
        >
          {direction === "asc" ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
        </Button>
      )}
    </div>
  );
}
